import { useCallback, useEffect, useRef, useState } from 'react';
import { getProgramDetail, getUniversityDetail } from '../api.js';

/**
 * Load the full record behind a search result.
 *
 * `kind` is `'programs'` or `'universities'`; `id` is the id from the Typesense
 * hit, or `null` when nothing is open. Returns `{ data, loading, error, reload }`.
 *
 * Search stays entirely on Typesense — this hook is the only thing that touches
 * the MongoDB-backed detail API, and only while a result is open.
 *
 * A monotonic request id guards against a slow response overwriting a newer one
 * (the same guard `useProgramSearch` uses): opening B while A is still in flight
 * must not show A's record in B's drawer. The in-flight request is also aborted
 * when `id` changes or the drawer closes.
 */
const FETCHERS = {
  programs: getProgramDetail,
  universities: getUniversityDetail,
};

export function useDetail(kind, id) {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);

  const requestId = useRef(0);
  const abortRef = useRef(null);

  const load = useCallback(() => {
    if (id == null) {
      setData(null);
      setError(null);
      setLoading(false);
      return undefined;
    }

    const fetcher = FETCHERS[kind];
    if (!fetcher) {
      setError('Unknown record type.');
      return undefined;
    }

    const rid = requestId.current + 1;
    requestId.current = rid;

    const controller = new AbortController();
    abortRef.current?.abort();
    abortRef.current = controller;

    // Clear the previous record so opening a different result shows its own
    // loading state rather than the last one's data for a frame.
    setData(null);
    setLoading(true);
    setError(null);

    fetcher(id, { signal: controller.signal })
      .then((result) => {
        if (requestId.current !== rid) return;
        setData(result);
      })
      .catch((err) => {
        if (requestId.current !== rid || controller.signal.aborted) return;
        setData(null);
        setError(err.message);
      })
      .finally(() => {
        if (requestId.current === rid) setLoading(false);
      });

    return () => controller.abort();
  }, [kind, id]);

  useEffect(() => load(), [load]);

  return { data, loading, error, reload: load };
}
